import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import type { CampLocation } from "@/types/campLocation";
import { db, auth } from "@/services/firebaseService";
import { getCampOffersForLocation } from "@/services/getCampOffersForLocation";
import { buildSuggestedOfferKeywords, buildUnsplashSeedQueries } from "../utils/unsplashQuerySeeds";
import { getUnsplashQuotaStatus, searchUnsplashPhotos, trackUnsplashDownload, UnsplashPhotoResult } from "@/services/unsplashProviderService";
import { uploadPhoto } from "@/services/photoStorageService";
import { createAndAttachCampPhotoFromStorageUpload, bestEffortIp, easternTimestampIsoNoMs, buildProviderPermissionInternalNote } from "@/services/campPhotoImportService";
import { useAuth } from "@/context/AuthContext";

function formatReset(ms: number | null): string {
  if (!ms) return "";
  try {
    return new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

function orgIdForLocation(location: CampLocation, locationId: string): string {
  const v: any = (location as any)?.providedByOrg;
  const orgId =
    typeof v === "string" ? v :
    (typeof v === "object" && v !== null ? String(v.id || "").trim() : "");
  return orgId || locationId; // consistent with PhotoUploadDialog fallback
}

function fileFromBlob(blob: Blob, baseName: string): File {
  const type = blob.type || "image/jpeg";
  const ext = type.includes("png") ? "png" : type.includes("webp") ? "webp" : "jpg";
  return new File([blob], `${baseName}.${ext}`, { type });
}

async function uploadPhotoAsPromise(file: File, orgId: string): Promise<{ downloadUrl: string; storagePath: string }> {
  return await new Promise((resolve, reject) => {
    uploadPhoto(
      file,
      orgId,
      undefined,
      (err) => reject(err),
      (downloadUrl, storagePath) => resolve({ downloadUrl, storagePath })
    );
  });
}

export function SearchProvidersModal(props: {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  location: CampLocation;
  onImported: () => void;
}) {
  const { isOpen, onClose, locationId, location, onImported } = props;
  const { userProfile } = useAuth();

  const [queries, setQueries] = useState<string[]>(["", "", "", "", ""]);
  const [quotaText, setQuotaText] = useState<string>("");
  const [quotaRemaining, setQuotaRemaining] = useState<number | null>(null);
  const [loadingSeeds, setLoadingSeeds] = useState(false);
  const [searching, setSearching] = useState(false);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [results, setResults] = useState<UnsplashPhotoResult[]>([]);
  const [limitedNote, setLimitedNote] = useState<string | null>(null);
  const [suggestedKeywords, setSuggestedKeywords] = useState<string[]>([]);

  const nonEmptyQueries = useMemo(() => queries.map((q) => q.trim()).filter(Boolean), [queries]);
  // Search fetches page 1 and may fetch page 2 to reach 50 results.
  const estimatedCallsMax = nonEmptyQueries.length * 2;

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const run = async () => {
      setLoadingSeeds(true);
      setLimitedNote(null);
      setResults([]);
      try {
        const quota = await getUnsplashQuotaStatus();
        if (!cancelled) {
          setQuotaRemaining(quota.poolRemainingApprox);
          const reset = quota.nextResetAtMs ? ` (resets ~${formatReset(quota.nextResetAtMs)})` : "";
          const remainingTxt = quota.poolRemainingApprox != null ? `${quota.poolRemainingApprox}` : "—";
          setQuotaText(`Unsplash: ${remainingTxt} calls remaining${reset}`);
        }

        const offers = await getCampOffersForLocation(db, locationId);
        const seeded = buildUnsplashSeedQueries({ location, offers });
        const next = [seeded[0] || "", seeded[1] || "", seeded[2] || "", "", ""];
        if (!cancelled) {
          setQueries(next);
          setSuggestedKeywords(buildSuggestedOfferKeywords({ location, offers, maxKeywords: 30 }));
        }
      } catch (e: any) {
        console.error("SearchProvidersModal init failed:", e);
        if (!cancelled) setQuotaText("Unsplash: quota unavailable");
      } finally {
        if (!cancelled) setLoadingSeeds(false);
      }
    };
    run();

    return () => { cancelled = true; };
  }, [isOpen, locationId, location]);

  const handleSearch = async () => {
    setSearching(true);
    setLimitedNote(null);
    try {
      const resp = await searchUnsplashPhotos({ queries: nonEmptyQueries, maxResults: 50 });
      setResults(resp.results || []);
      setQuotaRemaining(resp.quota.poolRemainingApprox);
      const reset = resp.quota.nextResetAtMs ? ` (resets ~${formatReset(resp.quota.nextResetAtMs)})` : "";
      const remainingTxt = resp.quota.poolRemainingApprox != null ? `${resp.quota.poolRemainingApprox}` : "—";
      setQuotaText(`Unsplash: ${remainingTxt} calls remaining${reset}`);
      if (typeof resp.limitedQueries === "number") {
        setLimitedNote(`Limited by Unsplash quota: searched ${resp.limitedQueries} query${resp.limitedQueries === 1 ? "" : "ies"}.`);
      }
      if ((resp.results || []).length === 0) {
        toast.message("No results found. Try different queries.");
      }
    } catch (e: any) {
      toast.error(e?.message || "Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleImport = async (p: UnsplashPhotoResult) => {
    if (importingId) return;
    setImportingId(p.id);
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) throw new Error("Authentication required");
      if (!userProfile || userProfile.role !== "admin_user") throw new Error("Admin access required");

      // Required by Unsplash: track download-like action
      const tracked = await trackUnsplashDownload({ downloadLocation: p.downloadLocation });
      setQuotaRemaining(tracked.quota.poolRemainingApprox);
      const reset = tracked.quota.nextResetAtMs ? ` (resets ~${formatReset(tracked.quota.nextResetAtMs)})` : "";
      const remainingTxt = tracked.quota.poolRemainingApprox != null ? `${tracked.quota.poolRemainingApprox}` : "—";
      setQuotaText(`Unsplash: ${remainingTxt} calls remaining${reset}`);

      const imgResp = await fetch(p.fullUrl);
      if (!imgResp.ok) throw new Error(`Failed to download image: ${imgResp.status}`);
      const blob = await imgResp.blob();
      const file = fileFromBlob(blob, `unsplash-${p.id}`);

      const orgId = orgIdForLocation(location, locationId);
      const { downloadUrl, storagePath } = await uploadPhotoAsPromise(file, orgId);

      const ip = await bestEffortIp();
      const now = new Date();
      const assertedAtIso = easternTimestampIsoNoMs(now);
      const permissionInternalNote = await buildProviderPermissionInternalNote({
        provider: "Unsplash",
        providerProfileUrl: p.photographerProfileUrl,
        providerPhotoId: p.id,
        providerPhotoPageUrl: p.photoPageUrl,
        assertedByEmail: firebaseUser.email || "unknown@email",
        assertedByUid: firebaseUser.uid,
        assertedAtIso,
        ipAddress: ip,
      });

      await createAndAttachCampPhotoFromStorageUpload({
        locationId,
        organizationId: orgId,
        storagePath,
        downloadUrl,
        yOffset: 0,
        caption: "",
        altText: `${location?.name || "Camp"} photo`,
        attribution: {
          photoReference: `Unsplash – ${p.photographerName}`,
          permissionStatus: "granted",
          permissionNote: "",
          permissionInternalNote,
        },
        locationAccessGroupId: userProfile.locationAccessGroupId || undefined,
      });

      toast.success("Photo imported");
      onImported();
    } catch (e: any) {
      console.error("Unsplash import failed:", e);
      toast.error(e?.message || "Import failed");
    } finally {
      setImportingId(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Search providers</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              {quotaText || "Unsplash: quota unavailable"}
              {quotaRemaining != null && estimatedCallsMax > 0 ? (
                <span className="ml-2">This search uses up to {estimatedCallsMax} call{estimatedCallsMax === 1 ? "" : "s"}.</span>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {queries.map((q, idx) => (
              <Input
                key={idx}
                value={q}
                placeholder={idx < 3 ? `Suggested query ${idx + 1}` : `Optional query ${idx + 1}`}
                onChange={(e) => {
                  const next = [...queries];
                  next[idx] = e.target.value;
                  setQueries(next);
                }}
                disabled={loadingSeeds || searching || !!importingId}
              />
            ))}
          </div>

          <div className="text-sm">
            <div className="text-muted-foreground">Suggested keywords</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {suggestedKeywords.length === 0 ? (
                <span className="text-xs text-muted-foreground">No keyword suggestions found for this location.</span>
              ) : suggestedKeywords.map((k) => (
                <span
                  key={k}
                  className="px-2 py-1 rounded-md border bg-muted/30 text-xs text-muted-foreground"
                >
                  {k}
                </span>
              ))}
            </div>
          </div>

          {limitedNote ? (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
              {limitedNote}
            </div>
          ) : null}

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {results.map((p) => (
              <button
                key={p.id}
                type="button"
                className="group rounded-md overflow-hidden border bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-kidsbooked-blue"
                onClick={() => void handleImport(p)}
                disabled={!!importingId || searching}
                title={`Use photo by ${p.photographerName}`}
              >
                <div className="aspect-[3/2] bg-gray-100">
                  <img src={p.smallUrl} alt={`Unsplash photo by ${p.photographerName}`} className="w-full h-full object-cover group-hover:scale-[1.01] transition-transform" />
                </div>
                <div className="p-2 text-left">
                  <div className="text-xs text-muted-foreground truncate">Unsplash – {p.photographerName}</div>
                  {importingId === p.id ? (
                    <div className="text-xs mt-1">Importing...</div>
                  ) : (
                    <div className="text-xs mt-1">Click to import</div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={searching || !!importingId}>
            Close
          </Button>
          <Button onClick={() => void handleSearch()} disabled={loadingSeeds || searching || nonEmptyQueries.length === 0 || !!importingId}>
            {searching ? "Searching..." : "Search now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

